<template>
  <div
    class="skeleton gradient"
    :class="{
      [backgroundColor]: !!backgroundColor,
      'gradient--paused': pauseAnimation,
    }" />
</template>

<script setup lang="ts">
import { useLayoutStore } from '../../layout.store';

export interface AiSkeletonProps {
  backgroundColor?: string;
  desktopWidth: string;
  desktopHeight: string;
  mobileWidth: string;
  mobileHeight: string;
  bottomMargin?: number;
  leftMargin?: number;
  borderRadius?: string;
}

const props = withDefaults(defineProps<AiSkeletonProps>(), {
  backgroundColor: 'neutral-100',
  borderRadius: '0px',
  bottomMargin: 8,
  desktopHeight: '0px',
  desktopWidth: '0px',
  leftMargin: 0,
  mobileHeight: '0px',
  mobileWidth: '0px',
});

const layoutStore = useLayoutStore();
const pauseAnimation = computed(() => layoutStore.pauseSkeletons);

const desktopWidth = computed(() => validateValue(props.desktopWidth));
const desktopHeight = computed(() => validateValue(props.desktopHeight));

const mobileWidth = computed(() => validateValue(props.mobileWidth));
const mobileHeight = computed(() => validateValue(props.mobileHeight));

const transformToRem = (number: number) => number / 10;
const validateValue = (value: string) => {
  if (value.includes('px')) {
    return `${transformToRem(parseInt(value.slice(0, value.length - 2)))}rem`;
  }

  return value;
};

const marginBottom = computed(() => `${props.bottomMargin}px`);
const marginLeft = computed(() => `${props.leftMargin}px`);
const borderRadius = computed(() => props.borderRadius);
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';

.skeleton {
  margin-bottom: v-bind('marginBottom');
  margin-left: v-bind('marginLeft');
  width: v-bind('mobileWidth');
  height: v-bind('mobileHeight');
  flex-shrink: 0;
  border-radius: v-bind(borderRadius);

  @media (mq.$from-medium) {
    width: v-bind('desktopWidth');
    height: v-bind('desktopHeight');
  }
}

.gradient {
  background: linear-gradient(
      to right,
      rgba(colors.$neutral-100, 0),
      rgba(colors.$neutral-300, 1) 50%,
      rgba(colors.$neutral-100, 0) 100%
    ),
    colors.$neutral-100;
  background-repeat: no-repeat;
  background-size: 300px 100%;
  background-position: -300px;
  animation: skeleton 2s linear infinite;

  &--paused {
    animation: none;
  }

  &.neutral-200 {
    background-color: colors.$neutral-200;
  }
}

@keyframes skeleton {
  0% {
    background-position: -300px;
  }
  100% {
    background-position: 1000px;
  }
}
</style>
